import React, { useState } from 'react'
import { Col, Row, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { ArrowDown2, ArrowUp2 } from 'iconsax-react'
import {
  AdminsEcommerceProductPricelistListResponseItem,
  AdminsEcommerceProductPricelistListResponsePriceItem,
  AdminsEcommerceProductPricelistListSortEnum,
  OrderDirectionEnum,
} from 'generated/api'
import IconSortAscDesc from 'components/tableHelpers/IconSortAscDesc'
import ButtonLink from 'components/button/Button'
import { rupiahFormat } from 'helpers/formatter'
import { thousandSeparator } from 'helpers/numbers'

interface PropsPricelistsTable {
  data: AdminsEcommerceProductPricelistListResponseItem[]
  loading: boolean
  orderDirection: OrderDirectionEnum
  orderColumn: AdminsEcommerceProductPricelistListSortEnum
  setSortingOrder: (columnKey: AdminsEcommerceProductPricelistListSortEnum) => void
  onClickEdit: (record: AdminsEcommerceProductPricelistListResponseItem) => void
}

interface ExapndedPriceListItemProps {
  record: AdminsEcommerceProductPricelistListResponseItem
}

interface PriceItemProps {
  item: AdminsEcommerceProductPricelistListResponsePriceItem
}

export default function PricelistsTable(props: PropsPricelistsTable) {
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])

  const handleExpand = (record: AdminsEcommerceProductPricelistListResponseItem) => {
    if (expandedRowKeys.includes(record.pricelistId)) {
      setExpandedRowKeys(prevKeys => prevKeys.filter(key => key !== record.pricelistId))
    } else {
      setExpandedRowKeys(prevKeys => [...prevKeys, record.pricelistId])
    }
  }

  const columnsStockPrice: ColumnsType<AdminsEcommerceProductPricelistListResponseItem> = [
    {
      title: (
        <div className="flex">
          <Typography.Text className="font-semibold text-gray-800">No.</Typography.Text>
        </div>
      ),
      width: '5%',
      dataIndex: 'nomor',
      key: 'nomor',
      render: (_text, _row, index) => (
        <div className="flex flex-col">
          <Typography.Text className="font-medium text-gray-800">{index + 1}.</Typography.Text>
        </div>
      ),
    },
    {
      title: (
        <div
          data-testid="column_head_product_name"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold text-gray-800">Nama Produk</Typography.Text>
          <IconSortAscDesc
            columnName={AdminsEcommerceProductPricelistListSortEnum.ProductName}
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '20%',
      dataIndex: 'productName',
      key: 'productName',
      render: text => (
        <div data-testid="column_body_product_name" className="flex flex-col">
          <Typography.Text className="font-semibold">{text}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () =>
            props.setSortingOrder(AdminsEcommerceProductPricelistListSortEnum.ProductName),
        }
      },
    },
    {
      title: (
        <div
          data-testid="column_head_stock"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold text-gray-800">Sisa Stok</Typography.Text>
          <IconSortAscDesc
            columnName={AdminsEcommerceProductPricelistListSortEnum.Stock}
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '15%',
      dataIndex: 'stock',
      key: 'stock',
      render: stock => (
        <div data-testid="column_body_stock" className="flex flex-col">
          <Typography.Text className="font-semibold">{thousandSeparator(stock)}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => props.setSortingOrder(AdminsEcommerceProductPricelistListSortEnum.Stock),
        }
      },
    },
    {
      title: (
        <div
          data-testid="column_head_product_price"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="font-semibold text-gray-800">Harga Produk</Typography.Text>
          <IconSortAscDesc
            columnName={AdminsEcommerceProductPricelistListSortEnum.Price}
            columnSortBy={props.orderDirection}
            columnSortField={props.orderColumn}
          />
        </div>
      ),
      width: '20%',
      dataIndex: 'prices',
      key: 'prices',
      render: priceMap => {
        const prices = priceMap.map(
          (price: AdminsEcommerceProductPricelistListResponsePriceItem) => price.price,
        )
        const smallestPrice = Math.min(...prices)
        const biggestPrice = Math.max(...prices)
        let renderText: string

        if (biggestPrice === smallestPrice) {
          renderText = `${rupiahFormat(smallestPrice)}`
        } else {
          renderText = `${rupiahFormat(smallestPrice)} - ${rupiahFormat(biggestPrice)}`
        }

        return (
          <div data-testid="column_body_price" className="flex flex-col">
            <Typography.Text className="font-semibold">{renderText}</Typography.Text>
          </div>
        )
      },
      onHeaderCell: () => {
        return {
          onClick: () => props.setSortingOrder(AdminsEcommerceProductPricelistListSortEnum.Price),
        }
      },
    },
    {
      title: (
        <div data-testid="column_head_action" className="flex text-center justify-center">
          <Typography.Text className="font-semibold text-center">Aksi</Typography.Text>
        </div>
      ),
      width: '15%',
      dataIndex: 'action',
      key: 'action',
      render: (_text, record) => (
        <div data-testid="column_body_action" className="flex flex-col">
          <ButtonLink
            data-testid="button_manage_stock"
            variant="outline-primary"
            block={true}
            label="UBAH"
            className="border font-bold"
            onClick={() => props.onClickEdit(record)}
          />
        </div>
      ),
    },
    {
      title: (
        <div data-testid="column_head_status" className="flex">
          <Typography.Text className="font-semibold">Status</Typography.Text>
        </div>
      ),
      width: '15%',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const isExpanded = expandedRowKeys.includes(record.pricelistId)
        return (
          <div data-testid="column_body_status" className="flex flex-row gap-2">
            <Typography.Text className="font-semibold">
              {text === 'ACTIVE' ? 'Aktif' : 'Tidak aktif'}
            </Typography.Text>
            <button
              data-testid="button_expand"
              className="bg-transparent border-0 flex hover:cursor-pointer items-center"
              onClick={() => handleExpand(record)}
            >
              {isExpanded ? <ArrowUp2 size={14} /> : <ArrowDown2 size={14} />}
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <Table
      rowKey="pricelistId"
      loading={props.loading}
      data-testid="tbl_stock-price"
      className="table-primary table-primary--admin sticky-table-header"
      columns={columnsStockPrice}
      dataSource={props.data}
      pagination={false}
      expandable={{
        expandedRowKeys,
        showExpandColumn: false,
        expandedRowRender: record => <ExpandedPriceListItem record={record} />,
      }}
    />
  )
}

function ExpandedPriceListItem(props: ExapndedPriceListItemProps) {
  const { record } = props
  const xsFlex = { flex: '100%' }
  const xlFlex = { flex: '20%' }

  const classNameExpandHeader = 'text-[#5B6573] p-2.5 bg-[#F9F9F9]'
  const classNameExpandBody =
    'text-[#333539] p-2.5 border-t border-0 border-[#E5E5E7] border-solid h-full bg-[#FAFBFC]'

  const propsCol = {
    xs: xsFlex,
    xl: xlFlex,
    className: 'flex flex-col',
  }

  const shownApps: string[] = [
    record.isShowMember ? 'Agen' : '',
    record.isShowWorker ? 'Pekerja' : '',
    record.isShowFarmer ? 'Petani' : '',
    record.isShowAmbassador ? 'Duta' : '',
  ].filter(Boolean)

  return (
    <Row className="font-semibold" data-testid="expanded_table">
      <Col {...propsCol}>
        <div className={classNameExpandHeader}>Periode Aktif</div>
        <div className={`${classNameExpandBody} xl:border-r`}>
          {`${dayjs(record.startDateUtc0).format('DD MMMM YYYY')} - ${dayjs(record.endDateUtc0).format('DD MMMM YYYY')}`}
        </div>
      </Col>
      <Col {...propsCol}>
        <div className={classNameExpandHeader}>Product Inquiry</div>
        <div className={`${classNameExpandBody} xl:border-r`}>
          {record.isInquiryAllowed ? 'Iya' : 'Tidak'}
        </div>
      </Col>
      <Col {...propsCol}>
        <div className={classNameExpandHeader}>Waktu Proses Barang (SLA)</div>
        <div className={`${classNameExpandBody} xl:border-r`}>
          {`${record.preparationDuration} Hari`}
        </div>
      </Col>
      <Col {...propsCol}>
        <div className={classNameExpandHeader}>Harga</div>
        <div className={`${classNameExpandBody} xl:border-r`}>
          {record.prices !== null &&
            record.prices.map((price, index) => (
              <div
                key={price.id}
                className={`${index + 1 < record.prices.length && price.minimumQuantity ? 'border-b border-solid border-0 border-[#E5E5E7]' : ''}`}
              >
                {price.minimumQuantity && <PriceItem item={price} />}
              </div>
            ))}
        </div>
      </Col>
      <Col {...propsCol}>
        <div className={classNameExpandHeader}>Ditampilkan Pada</div>
        <div className={`${classNameExpandBody}`}>
          {shownApps.map((shownApp, index) => (
            <div
              key={shownApp}
              className={`${index + 1 < shownApps.length ? 'border-b border-solid border-0 border-[#E5E5E7] ' : ''}max-w-fit`}
            >
              <ShownsApp app={shownApp} />
            </div>
          ))}
        </div>
      </Col>
    </Row>
  )
}

function PriceItem(props: PriceItemProps) {
  const { item } = props

  return (
    <div className="xl:flex xl:justify-between">
      <span>
        {item.maximumQuantity
          ? `${item.minimumQuantity}-${item.maximumQuantity}`
          : `≥ ${item.minimumQuantity}`}
      </span>
      <span>{rupiahFormat(item.price)}</span>
    </div>
  )
}

function ShownsApp({ app }: { app: string }) {
  return (
    <div className="flex gap-4">
      <img src="/media/logoSawitpro.png" width={16} height={16} className="self-center" />
      <span>Aplikasi {app}</span>
    </div>
  )
}
